import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-locale';
import FormField from '@adsk/alloy-react-form-field';
import SelectBox from '@adsk/alloy-select-box';
import { COMPONENT_VARIANTS } from '@adsk/acs-admin-ui-components-helpers';
import { useStore } from '../../../StoreProvider';
import EmailPill from '../EmailPill';
import EmailOption from '../EmailOption';
import { isUserInAccount, isUserInProject } from '../../utils';
import { fieldStyle } from '../../styles';
import { ACTIONS } from '../../state/actions';
import {
  getEmailInvalidErrorMessage,
  validateRepeatedEmails,
  getEmailsToAdd,
} from './utils';
import { customEmailSelectBoxStyle, emailManuStyle } from './styles';

const EmailField = ({
  accountUsers,
  allowReinviting,
  isInvalidEmailField,
  onChangeEmailInput,
  projectUsers,
  variant,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const { state, dispatch } = useStore();
  const emailFieldErrorMessage = getEmailInvalidErrorMessage(
    state.numberOfInvalidEmails + state.numberOfDuplicatedEmails,
    T.translate('INVALID_EMAIL_ERROR'),
    T.translate('MULTIPLE_INVALID_EMAILS_ERROR')
  );

  // workaround to detect delete key and remove last email
  const prefixToDetectDeleteKey = ' ';
  const emailInputValue =
    state.emailSearchInput === prefixToDetectDeleteKey &&
    state.emails.length === 0
      ? ''
      : state.emailSearchInput;

  const changeEmails = (emails) => {
    dispatch({
      type: ACTIONS.SET_EMAILS,
      payload: emails,
    });
  };

  const changeEmailInput = (value) => {
    dispatch({
      type: ACTIONS.SET_EMAIL_SEARCH_INPUT,
      payload: value,
    });
  };

  const latestOnChangeEmailInput = useRef(onChangeEmailInput);
  useEffect(() => {
    latestOnChangeEmailInput.current = onChangeEmailInput;
  }, [onChangeEmailInput]);

  useEffect(() => {
    const handler = setTimeout(() => {
      latestOnChangeEmailInput.current(emailInputValue.trim());
    }, 800);

    return () => clearTimeout(handler);
  }, [emailInputValue]);

  const handleRemoveEmail = ({ value, valid, duplicated }) => {
    const emails = state.emails.filter((email) => email.value !== value);
    const numberOfInvalidEmails = valid
      ? state.numberOfInvalidEmails
      : state.numberOfInvalidEmails - 1;
    const numberOfDuplicatedEmails = duplicated
      ? state.numberOfDuplicatedEmails
      : state.numberOfDuplicatedEmails - 1;
    changeEmails(
      validateRepeatedEmails({
        emails,
        numberOfInvalidEmails,
        numberOfDuplicatedEmails,
      })
    );
  };

  const addEmails = (inputEmails) => {
    changeEmailInput(prefixToDetectDeleteKey);
    const { emailsToAdd, numberOfInvalidEmailsToAdd } = getEmailsToAdd(
      state.emails,
      inputEmails
    );

    if (emailsToAdd.length > 0) {
      changeEmails(
        validateRepeatedEmails({
          emails: [...state.emails, ...emailsToAdd],
          numberOfInvalidEmails:
            state.numberOfInvalidEmails + numberOfInvalidEmailsToAdd,
        })
      );
    }
  };

  const handleEmailInputChange = (value) => {
    if (state.emails.length > 0 && value === '') {
      // this case is part of a workaround to detect delete key and remove last email
      const lastEmail = state.emails[state.emails.length - 1];
      handleRemoveEmail(lastEmail);
      return;
    }

    let originalValue = value;

    if (state.emails.length > 0) {
      // remove prefix to detect delete key from value to be able split by space
      // this is caused for the workaround to detect delete key and remove last email
      originalValue = value.substring(1);
    }

    if (originalValue.includes(',')) {
      const commaSeparatedEmails = originalValue.split(',');
      addEmails(commaSeparatedEmails);
      return;
    }

    if (originalValue.includes(';')) {
      const semicolonSeparatedEmails = originalValue.split(';');
      addEmails(semicolonSeparatedEmails);
      return;
    }

    if (originalValue.includes(' ')) {
      const blankSeparatedEmails = originalValue.split(' ');
      addEmails(blankSeparatedEmails);
      return;
    }

    changeEmailInput(value);
  };

  return (
    <div css={fieldStyle} data-testid="InviteProjectMemberModal_email_field">
      <FormField
        error={emailFieldErrorMessage}
        errorVariant={FormField.ERROR_VARIANTS.TOP}
        label={T.translate('EMAIL_LABEL')}
        labelVariant={FormField.LABEL_VARIANTS.TOP}
        required
      >
        <SelectBox
          creatable
          inputDebounce={400}
          inputValue={emailInputValue}
          invalid={isInvalidEmailField}
          isOptionDisabled={(option) =>
            (variant === COMPONENT_VARIANTS.ACCOUNT &&
              isUserInAccount(option.email, accountUsers)) ||
            (!allowReinviting && isUserInProject(option.email, projectUsers))
          }
          labelKey="email"
          menuStyle={emailManuStyle}
          multiple
          noResultsPlaceholder={variant !== COMPONENT_VARIANTS.ACCOUNT}
          onChange={(option) => addEmails([option[option.length - 1].email])}
          onClose={() => addEmails([state.emailSearchInput])}
          onCreate={(email) => addEmails([email])}
          onInputChange={handleEmailInputChange}
          optionRenderer={(option) => (
            <EmailOption
              name={option.name}
              email={option.email}
              variant={variant}
              allowReinviting={allowReinviting}
              userInProject={isUserInProject(option.email, projectUsers)}
            />
          )}
          options={emailInputValue !== '' ? accountUsers : []}
          placeholder={T.translate('EMAIL_PLACEHOLDER')}
          searchable
          searchPlaceholder={null}
          showValueAsTags
          style={customEmailSelectBoxStyle}
          value={state.emails}
          valueKey="email"
          valueTagRenderer={(email) => (
            <EmailPill
              key={email.value}
              email={email}
              onRemoveEmail={handleRemoveEmail}
            />
          )}
        />
      </FormField>
    </div>
  );
};

EmailField.propTypes = {
  accountUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  allowReinviting: PropTypes.bool,
  isInvalidEmailField: PropTypes.bool.isRequired,
  onChangeEmailInput: PropTypes.func.isRequired,
  projectUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  variant: PropTypes.oneOf(Object.values(COMPONENT_VARIANTS)),
};

EmailField.defaultProps = {
  allowReinviting: false,
  variant: COMPONENT_VARIANTS.PROJECT,
};

export default EmailField;
